import { Suspense, useEffect, useState } from 'react';
import { DesktopShell } from './layouts/DesktopShell.jsx';
import { MobileShell } from './layouts/MobileShell.jsx';
import { DesktopAuthLoading, DesktopAuthScreen } from '../features/auth/desktop/DesktopAuthScreen.jsx';
import { MobileAuthLoading, MobileAuthScreen } from '../features/auth/mobile/MobileAuthScreen.jsx';
import { useAuthSession } from '../features/auth/hooks/useAuthSession.js';
import { DesktopEntryGate } from '../features/entry/desktop/DesktopEntryGate.jsx';
import { MobileEntryGate } from '../features/entry/mobile/MobileEntryGate.jsx';
import { MobileInstallPrompt } from '../features/pwa/shared/MobileInstallPrompt.jsx';
import { StandaloneSiteSupport } from '../features/union-support/shared/StandaloneSiteSupport.jsx';
import { useEntryGate } from '../features/entry/hooks/useEntryGate.js';
import { useViewportMode } from '../shared/hooks/useViewportMode.js';
import { ControlBoot } from '../shared/ui/LoadingState.jsx';
import { lazyRoute, RouteErrorBoundary } from '../shared/ui/LazyRoute.jsx';
import { resetUnionSession } from '../shared/browser/unionSessionBoundary.js';

// 중계방은 로그인 없이 들어갈 수 있어야 해서 인증 분기보다 위에서 처리한다. 중계방을 쓰지 않는
// 사용자가 유튜브 플레이어와 중계방 채팅 코드를 받지 않도록 따로 떼어 둔다.
// lazy 가 아니라 lazyRoute 다. 배포가 올라가면 이미 열려 있던 화면이 들고 있는 청크
// 파일명이 사라져 동적 import 가 거절되는데, 그냥 lazy 로 두면 경계가 없어 트리가 통째로
// 내려가 검은 화면이 된다("중계방에 들어가면 검은 화면, 새로고침하면 정상").
const BroadcastPage = lazyRoute(() => import('../features/broadcast/shared/BroadcastPage.jsx'));

function MobileExperience({ children }) {
  return <><MobileInstallPrompt />{children}</>;
}

function routeName() {
  if (typeof window === 'undefined') return '';
  return window.location.hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '');
}

function isBroadcastRoute() {
  return routeName() === 'broadcast';
}

function isSupportRoute() {
  if (typeof window === 'undefined') return false;
  return window.location.hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '') === 'support';
}

export default function App() {
  const viewportMode = useViewportMode();
  const { session, user, loading, signOut } = useAuthSession();
  const entry = useEntryGate(user?.id || '');
  const isMobile = viewportMode === 'mobile';
  const [supportRoute, setSupportRoute] = useState(isSupportRoute);
  const [broadcastRoute, setBroadcastRoute] = useState(isBroadcastRoute);

  useEffect(() => {
    if (!user?.id) return undefined;
    const handleTransfer = event => {
      if (event.key === `dororaid:union-transfer:${user.id}` && event.newValue) resetUnionSession('other-tab-transfer');
    };
    window.addEventListener('storage', handleTransfer);
    return () => window.removeEventListener('storage', handleTransfer);
  }, [user?.id]);

  useEffect(() => {
    const handleHashChange = () => {
      setSupportRoute(isSupportRoute());
      setBroadcastRoute(isBroadcastRoute());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const closeStandaloneSupport = () => {
    window.location.hash = '';
    setSupportRoute(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  if (loading) return isMobile ? <MobileAuthLoading /> : <DesktopAuthLoading />;

  if (broadcastRoute) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<ControlBoot stage="union" />}>
          <BroadcastPage mobile={isMobile} onClose={() => { window.location.hash = ''; setBroadcastRoute(false); }} />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  if (!session) {
    return isMobile
      ? <MobileExperience><MobileAuthScreen /></MobileExperience>
      : <DesktopAuthScreen />;
  }

  if (entry.mode === 'loading') return <ControlBoot stage="union" />;

  let experience;
  if (entry.mode !== 'union' && supportRoute) {
    experience = isMobile
      ? <MobileExperience><StandaloneSiteSupport mobile onClose={closeStandaloneSupport} /></MobileExperience>
      : <StandaloneSiteSupport onClose={closeStandaloneSupport} />;
  } else if (entry.mode !== 'union') {
    experience = isMobile
      ? <MobileExperience><MobileEntryGate user={user} onSignOut={signOut} entry={entry} /></MobileExperience>
      : <DesktopEntryGate user={user} onSignOut={signOut} entry={entry} />;
  } else {
    experience = isMobile
      ? <MobileExperience><MobileShell user={user} onSignOut={signOut} entry={entry} /></MobileExperience>
      : <DesktopShell user={user} onSignOut={signOut} entry={entry} />;
  }

  const unionId = entry.mode === 'union' ? String(entry?.union?.id || '') : '';
  const nickname = entry.mode === 'union' ? String(entry?.profile?.nickname || '멤버') : '';
  return experience;
}
